import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Onboarding, OnboardingStatus } from '../generated/gql-types';
import { onboardingActions } from './onboarding.action';
import { getTokenSecurely } from '../services/tokenService';
import { fetchCurrentUserOnMobile } from '../user/user.action';
import { useUserStore } from '../user/useUserStore';
import { upsertOne } from '../util/zustand.util';

type OnboardingStore = {
  isLoading: boolean;
  onboarding: Onboarding | null;
  userOnboardings: Onboarding[];
  fetchUserOnboarding: (userId: string) => Promise<Onboarding>;
  fetchOnboarding: () => any;
  setOnboarding: (onboarding: Onboarding | null) => void;
  fetchAndSetOnboarding: () => Promise<null | undefined>;
  currentPageId: number;
  setCurrentPageId: (pageId: number) => void;
  getOnboardingForUser: (userId: string) => Onboarding | null;
};

export const useOnboardingStore = create<OnboardingStore>()(
  persist(
    (set, get) => ({
      //   state
      onboarding: null,
      userOnboardings: [],
      isLoading: false,
      currentPageId: 0,
      setCurrentPageId: (pageId: number) => set({ currentPageId: pageId }),

      //   setters
      setOnboarding: (onboarding: Onboarding | null) => set({ onboarding }),

      //   actions
      fetchOnboarding: async () => {
        try {
          set({ isLoading: true });
          const onboarding = await onboardingActions.fetchOnboarding();
          set({ isLoading: false });
          return onboarding;
        } catch (error) {
          console.error('Error fetching onboarding:', error);
          set({ isLoading: false });
          return null;
        }
      },

      fetchUserOnboarding: async (userId: string): Promise<Onboarding> => {
        try {
          set({ isLoading: true });
          const onboarding =
            await onboardingActions.fetchOnboardingForUser(userId);
          set({
            userOnboardings: upsertOne(
              get().userOnboardings,
              onboarding,
              'userId',
            ) as unknown as Onboarding[],
          });
          set({ isLoading: false });
          return onboarding;
        } catch (error) {
          console.error('Error fetching onboarding for user:', error);
          set({ isLoading: false });
          throw error;
        }
      },

      fetchAndSetOnboarding: async () => {
        try {
          set(() => ({ isLoading: true }));
          const onboarding = await onboardingActions.fetchAndSetOnboarding();

          if (onboarding?.currentPageId !== undefined) {
            set({ currentPageId: Number(onboarding.currentPageId) });
          }
          set({ onboarding });
          if (onboarding?.status === OnboardingStatus.Completed) {
            const token = await getTokenSecurely();
            if (token) {
              const user = await fetchCurrentUserOnMobile({ networkOnly: true });
              if (user) {
                useUserStore.getState().setCurrentUser(user);
              }
            }
          }
          set({ isLoading: false });
        } catch (error) {
          console.error('Error fetching onboarding:', error);
          set({ onboarding: null, isLoading: false });
          return null;
        }
      },

      //   getters
      getOnboardingForUser: (userId: string) => {
        return (
          get().userOnboardings.find(
            (onboarding) => onboarding.userId === userId,
          ) || null
        );
      },
    }),
    {
      name: 'onboarding-storage',
      storage: createJSONStorage(() => AsyncStorage),
      partialize: (state) => ({
        onboarding: state.onboarding,
        userOnboardings: state.userOnboardings,
        currentPageId: state.currentPageId,
      }),
    },
  ),
);
